
import { Clock, MapPin, Users, Timer, AlertCircle, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Queue } from "@/types/queue";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useProfileData } from "@/hooks/useProfileData";

interface QueueCardProps {
  queue: Queue;
  onEdit: (queue: Queue) => void;
  onClick: (queue: Queue) => void;
  onDelete: (queue: Queue) => void;
}

export function QueueCard({ queue, onEdit, onClick, onDelete }: QueueCardProps) {
  const { companyName, companyAddress } = useProfileData();
  
  const formatTime = (minutes: number | null) => {
    if (!minutes) return "0 min";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const handleClick = () => {
    if (queue.is_active) {
      onClick(queue);
    }
  };

  return (
    <Card 
      className={`group relative overflow-hidden transition-all duration-300 ${
        queue.is_active ? 'cursor-pointer hover:shadow-lg' : 'cursor-not-allowed opacity-75'
      }`}
      onClick={handleClick}
    >      
      <div className="absolute top-4 right-4 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {queue.is_active && (
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(queue);
            }}
          >
            Edit
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(queue);
          }}
        >
          <Trash2 size={16} />
        </Button>
      </div>

      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h3 className="text-2xl font-bold tracking-tight text-foreground">
                  {queue.name}
                </h3>
                {!queue.is_active && (
                  <Badge variant="outline" className="text-muted-foreground">
                    Inactive
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center text-muted-foreground">
              <MapPin size={16} className="mr-2 shrink-0" />
              <span className="text-sm truncate">{companyAddress || "Address not specified"}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 p-4 rounded-xl bg-secondary shadow-sm border border-muted/30">
              <div className="flex items-center justify-center text-accent">
                <Clock size={20} />
              </div>
              <div className="text-2xl font-bold text-center text-foreground animate-number-change">
                #{queue.current_number || 0}
              </div>
              <div className="text-xs text-center text-muted-foreground font-medium">
                Current Number
              </div>
            </div>

            <div className="space-y-2 p-4 rounded-xl bg-secondary shadow-sm border border-muted/30">
              <div className="flex items-center justify-center text-accent">
                <Users size={20} />
              </div>
              <div className="text-2xl font-bold text-center text-foreground animate-number-change">
                {queue.people_in_queue || 0}
              </div>
              <div className="text-xs text-center text-muted-foreground font-medium">
                In Queue
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 rounded-lg p-3 border border-muted/20">
              <Timer size={16} className="shrink-0 text-accent" />
              <span>~{formatTime(queue.avg_wait_time)} wait</span>
            </div>
          </div>

          {queue.description && (
            <div className="text-sm text-muted-foreground border-t border-muted/20 pt-4">
              <p className="line-clamp-2">{queue.description}</p>
            </div>
          )}

          <div className="text-xs text-muted-foreground flex items-center gap-1 bg-secondary/80 rounded-full px-3 py-1.5 w-fit border border-muted/20">
            <AlertCircle size={12} className="text-accent" />
            <span>Updated {format(new Date(queue.last_updated), 'HH:mm')}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
